import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { fetchPeopleLikeMeRecommendations, fetchPersonalisedAlternativeOffers } from 'actions/RecommendationActions'

import { useAppDispatch } from 'hooks/reduxHooks'
import Heading from 'components/Luxkit/Typography/Heading'
import { without } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LayoutContainer from 'components/Common/LayoutContainer'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getHighIntentOfferIds } from 'selectors/recomendations/highIntentSelectors'
import OfferCarouselHeader from 'home/components/OfferCarousels/common/OfferCarouselHeader'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'

const SectionContainer = styled(VerticalSpacer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

const Container = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.seven};
  padding: ${rem(20)};
  width: ${rem(320)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(40)};
  }

  ${mediaQueryUp.largeDesktop} {
    padding: ${rem(60)};
  }

`

interface Props {
  userGivenName?: string;
  highIntentOfferIds: string[];
  alternatives: App.RecommendationState['personalisedAlternatives'];
  peopleLikeMe: App.RecommendationState['personalisedPeopleLikeMe'];
}

/**
 * Top pick for me carousel
 *
 * The offers are selected based on (fallback to next if no recos)
 * 1. personalised alternative
 * 2. people like me
 */
function TopPicksForMeCarousel({
  userGivenName,
  highIntentOfferIds,
  alternatives,
  peopleLikeMe,
}: Props) {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchPersonalisedAlternativeOffers())
    dispatch(fetchPeopleLikeMeRecommendations())
  }, [dispatch])

  const alternativeLoading = alternatives.state === 'loading'
  const canUseAlternative = !alternativeLoading && alternatives.offers.length > 0
  const plmLoading = peopleLikeMe.state === 'loading'

  const recommendationsLoading = alternativeLoading || (!canUseAlternative && plmLoading)

  const recommendedOfferIds = useMemo(() => {
    let condidates: string[] = []

    if (!recommendationsLoading) {
      // use alt first, then plm as fallback
      if (canUseAlternative) {
        condidates = alternatives.offers.map(offer => offer.offerId)
      }
      else {
        condidates = peopleLikeMe.offers.map(offer => offer.offerId)
      }
    }

    return without(condidates, ...highIntentOfferIds)
  }, [highIntentOfferIds, peopleLikeMe.offers, alternatives.offers, canUseAlternative, recommendationsLoading])

  const offerList = useMemo((): App.OfferList => ({
    fetching: recommendationsLoading,
    error: undefined,
    offerIds: recommendedOfferIds,
    key: 'topPicks',
  }), [recommendationsLoading, recommendedOfferIds])

  if (!recommendationsLoading && recommendedOfferIds.length === 0) {
    return null
  }

  return (
    <SectionContainer gap={20}>
      <LayoutContainer>
        <OfferCarouselHeader allTo="/top-picks-for-me">
          Top picks for <i>{userGivenName || 'you'}</i>
        </OfferCarouselHeader>
      </LayoutContainer>
      <OfferListCarousel
        overrideOfferList={offerList}
        mobileLimit={8}
      >
        <Container direction="vertical" horizontalAlign="center" verticalAlign="center" gap={24}>
          <VerticalSpacer gap={8}>
            <Heading align="center" variant="heading5">Looking for something else?</Heading>
            <BodyText align="center" variant="medium">Discover amazing curated offers tailored to you.</BodyText>
          </VerticalSpacer>
          <TextButton kind="primary" size="medium" shape="square" to="/top-picks-for-me">
            View all
          </TextButton>
        </Container>
      </OfferListCarousel>
    </SectionContainer>
  )
}

function mapState(state: App.State) {
  return {
    userGivenName: state.auth.account.givenName,
    highIntentOfferIds: getHighIntentOfferIds(state),
    alternatives: state.recommendations.personalisedAlternatives,
    peopleLikeMe: state.recommendations.personalisedPeopleLikeMe,
  }
}

export default connect(mapState)(TopPicksForMeCarousel)
